import CloseIcon from '@mui/icons-material/Close';
import DoneIcon from '@mui/icons-material/Done';
import { Button, Checkbox, FormControlLabel, Stack } from '@mui/material';
import InputBase from '@mui/material/InputBase';
import Paper from '@mui/material/Paper';
import { Theme } from '@mui/material/styles';
import * as React from 'react';
import { makeStyles } from 'tss-react/mui';
import { useEditNote } from '../../../sic-lib/api/hooks/useNotes';
import { ContactNoteStatus } from '../../../sic-lib/enums/ContactNoteStatus';
import { GenericReducer, genericReducer } from '../../../sic-lib/reducers/genericReducer';
import { Contact } from '../../../sic-lib/types/Contact';
import { ContactNote } from '../../../sic-lib/types/ContactNote';
import AxiosErrorAlert from '../../Shared/Alerts/AxiosErrorAlert';
import ButtonLoading from '../../Shared/ButtonLoading';

interface Props {
    note: ContactNote;
    contact: Contact;
    onToggleHandler: () => void;
}

export default function ContactNoteEdit({ note, contact, onToggleHandler }: Props) {
    const { classes } = useStyles();

    const {
        mutateAsync: editNote,
        error,
        isLoading,
        isSuccess,
        isError,
    } = useEditNote(contact.contactId);

    const [compState, compDispatch] = React.useReducer<GenericReducer<ContactNote>>(
        genericReducer,
        {
            ...note,
        },
    );

    const handleChangePin = () => {
        compState.noteStatus === ContactNoteStatus.Pinned
            ? compDispatch({ noteStatus: ContactNoteStatus.Active })
            : compDispatch({ noteStatus: ContactNoteStatus.Pinned });
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        compDispatch({ text: event.target.value });
    };

    const handleSubmit = async () => {
        await editNote({
            ...compState,
        });
        onToggleHandler();
    };

    return (
        <Paper
            className={
                compState.noteStatus === ContactNoteStatus.Pinned ? classes.pinned : classes.paper
            }
        >
            <Stack spacing={1}>
                <InputBase
                    id="text_edit_note"
                    name="text"
                    error={!!error}
                    value={compState.text}
                    onChange={handleChange}
                    fullWidth
                    multiline
                    autoFocus
                    sx={{ p: 0, lineHeight: 1.5 }}
                />
                <AxiosErrorAlert error={error} />
                <Stack direction="row" spacing={1} justifyContent="space-between">
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={compState.noteStatus === ContactNoteStatus.Pinned}
                                onChange={handleChangePin}
                                color="primary"
                                size="small"
                            />
                        }
                        label="Pinned"
                    />
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Button
                            aria-label="Cancel"
                            size="small"
                            onClick={onToggleHandler}
                            variant="outlined"
                            startIcon={<CloseIcon />}
                        >
                            Cancel
                        </Button>
                        <ButtonLoading
                            aria-label="Save"
                            onClick={handleSubmit}
                            color="success"
                            variant="outlined"
                            size="small"
                            startIcon={<DoneIcon />}
                            isLoading={isLoading}
                            isSuccess={isSuccess}
                            isError={isError}
                        >
                            Save
                        </ButtonLoading>
                    </Stack>
                </Stack>
            </Stack>
        </Paper>
    );
}

const useStyles = makeStyles()((theme: Theme) => ({
    paper: {
        padding: theme.spacing(2),
        backgroundColor: theme.palette.noteStatus.main,
        //border: `1px solid ${theme.palette.divider}`
    },
    pinned: {
        padding: theme.spacing(2),
        backgroundColor: theme.palette.noteStatus.light,
    },
    btnSave: {
        color: theme.palette.conversationTimelinePaper.light,
    },
}));
